import { z } from 'zod';

/**
 * Facilitation settings, mirroring the three sections of the in-retro settings
 * panel (Comments / Group / Vote).
 */

export const AI_SCOPES = ['SAME_COLUMN', 'ACROSS_COLUMNS'] as const;
export type AiScope = (typeof AI_SCOPES)[number];

export const commentSettingsSchema = z.object({
  /** Allow attaching a GIF to a comment. */
  allowGifs: z.boolean(),
  /** null = unlimited. */
  maxCommentsPerParticipant: z.number().int().min(1).max(50).nullable(),
});

export const groupSettingsSchema = z.object({
  restrictGroupingToFacilitators: z.boolean(),
  restrictFilteringToFacilitators: z.boolean(),
  restrictSortingToFacilitators: z.boolean(),
  restrictHighlightingToFacilitators: z.boolean(),
  /** Let participants keep adding comments after the reveal. */
  allowNewComments: z.boolean(),
  allowReactions: z.boolean(),
  aiSuggestions: z.boolean(),
  aiScope: z.enum(AI_SCOPES),
});

export const voteSettingsSchema = z.object({
  votesPerParticipant: z.number().int().min(1).max(20),
  /** null = no per-target cap, so all votes may go on one card. */
  maxVotesPerTarget: z.number().int().min(1).max(20).nullable(),
  /** null = every column is votable. Otherwise only these column ids are. */
  votableColumnIds: z.array(z.string()).nullable(),
});

export const retroSettingsSchema = z.object({
  comments: commentSettingsSchema,
  group: groupSettingsSchema,
  vote: voteSettingsSchema,
});

export type CommentSettings = z.infer<typeof commentSettingsSchema>;
export type GroupSettings = z.infer<typeof groupSettingsSchema>;
export type VoteSettings = z.infer<typeof voteSettingsSchema>;
export type RetroSettings = z.infer<typeof retroSettingsSchema>;

export const DEFAULT_RETRO_SETTINGS: RetroSettings = {
  comments: {
    allowGifs: false,
    maxCommentsPerParticipant: null,
  },
  group: {
    // Grouping is editorial work on other people's cards: dissolving a group or
    // renaming it changes what everybody else is looking at, and what the vote
    // step will offer. Facilitator-only by default; a facilitator who wants a
    // collaborative session can turn this off in the settings panel mid-retro.
    restrictGroupingToFacilitators: true,
    restrictFilteringToFacilitators: false,
    restrictSortingToFacilitators: false,
    restrictHighlightingToFacilitators: false,
    allowNewComments: true,
    allowReactions: true,
    aiSuggestions: true,
    aiScope: 'SAME_COLUMN',
  },
  vote: {
    votesPerParticipant: 3,
    maxVotesPerTarget: null,
    votableColumnIds: null,
  },
};

/**
 * Settings are stored as JSON, so rows written by an older build may be missing
 * keys. Parsing through the defaults keeps old retros loadable after we add a
 * new setting.
 */
export function parseSettings(raw: unknown): RetroSettings {
  const result = retroSettingsSchema.safeParse(raw);
  if (result.success) return result.data;

  const partial = (raw ?? {}) as Partial<Record<keyof RetroSettings, unknown>>;
  return retroSettingsSchema.parse({
    comments: { ...DEFAULT_RETRO_SETTINGS.comments, ...(partial.comments as object) },
    group: { ...DEFAULT_RETRO_SETTINGS.group, ...(partial.group as object) },
    vote: { ...DEFAULT_RETRO_SETTINGS.vote, ...(partial.vote as object) },
  });
}
